import { useQuery } from '@tanstack/react-query';
import { getLocationVerificationCount } from '../api/locations.api';
import { queryKeys } from '../api/queryKeys';
import type { LocationVerificationCountResponse } from '../types/api/location.types';

const ZERO_COUNT: LocationVerificationCountResponse = {
  locations: 0,
  agents: 0,
  vDrive: 0,
  brands: 0,
};

/**
 * Pending-verification counts (locations / agents / vDrive / brands) shown
 * in the admin sidebar badges.
 *
 * Refresh semantics — tuned for "admin sees fresh-ish numbers without
 * pounding the endpoint":
 *
 *   - `staleTime: 30s`     — short-lived freshness; navigations within 30s
 *                            don't refetch.
 *   - `refetchInterval: 5min` — polls every 5 minutes while the user has
 *                            the app open, so the sidebar updates even
 *                            when verifications are completed in another
 *                            tab / by another admin.
 *   - `refetchOnWindowFocus: true` (global default) — alt-tabbing back to
 *                            the app forces a fresh read.
 *
 * After a verify/reject action, the relevant `*Details` page invalidates
 * `queryKeys.admin.verifications.count` via `invalidateByKey(...)`, which
 * also forces an immediate refetch independent of the interval.
 *
 * Replaces the previous Zustand `useVerifications.getCount` pattern, which
 * mixed server cache state into a client-side store and required manual
 * `getCount()` calls scattered across handlers.
 */
export function useVerificationsCount(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const query = useQuery({
    queryKey: queryKeys.admin.verifications.count,
    queryFn: async () => {
      const { data } = await getLocationVerificationCount();
      return data.data;
    },
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: enabled ? 5 * 60 * 1000 : false,
  });

  return {
    /** Always defined — falls back to all-zeros until the first response. */
    count: query.data ?? ZERO_COUNT,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
